import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Points } from '@styles';
import { SlideContent } from './slide-content';
import { FinalSlide } from './final-slide';
import { IS_CLIENT } from '@constants';

export const Speeches = ({ slides = []}) => {
  const [SwiperComponent, setSwiperComponent] = useState(null);

  const calcXRef = useRef(null);
  const [marginLeft, setMarginLeft] = useState(0);
  const [marginRight, setMarginRight] = useState(0);
  const [countSlide, setCountSlide] = useState(1.85);
  const [finalSlideOffset, setFinalSlideOffset] = useState(0);

  function getCountSlide(win) {
    if (!IS_CLIENT) return;

    if (!win || !win.innerWidth) return;

    if (win.innerWidth >= 1920) {
      return 2.45;
    } else if (win.innerWidth >= 1440) {
      return 1.85;
    } else if (win.innerWidth >= 1024) {
      return 1.65;
    } else {
      return 1.35;
    }
  }

  function getFinalSlideOffset(win, slidesPerView) {
    if (!IS_CLIENT) return 0;

    if (!win || !win.innerWidth) return 0;

    let finalSlideWidth;

    if (win.innerWidth >= 1440) {
      finalSlideWidth = 400;
    } else if (win.innerWidth >= 1024) {
      finalSlideWidth = 300;
    } else {
      return 0;
    }

    const spaceBetween = 20;
    const slotWidth = (win.innerWidth - (slidesPerView - 1) * spaceBetween) / slidesPerView;
    const diff = slotWidth - finalSlideWidth;

    return diff > 0 ? -diff : 0;
  }

  useEffect(() => {
    if (calcXRef.current) {
      const { x, right } = calcXRef.current.getBoundingClientRect();

      setMarginLeft(x);
      setMarginRight(window.innerWidth - right);
    }
  }, [calcXRef]);

  useEffect(() => {
    if (!IS_CLIENT) return;

    const initialCountSlide = getCountSlide(window);

    setCountSlide(initialCountSlide);
    setFinalSlideOffset(getFinalSlideOffset(window, initialCountSlide));

    window.addEventListener('resize', (event) => {
      if (!calcXRef.current) return;

      const { x, right } = calcXRef.current.getBoundingClientRect();
      const newCountSlide = getCountSlide(event.target);

      setMarginLeft(x);
      setMarginRight(event.target.innerWidth - right);
      setCountSlide(newCountSlide);
      setFinalSlideOffset(getFinalSlideOffset(event.target, newCountSlide));
    });

    const loadSwiper = async () => {
      try {
        const SwiperModule = await import('swiper/react');
        const Modules = await import('swiper');

        setSwiperComponent({
          Swiper: SwiperModule.Swiper,
          SwiperSlide: SwiperModule.SwiperSlide,
          Navigation: Modules.Navigation,
          Pagination: Modules.Pagination,
          Autoplay: Modules.Autoplay,
        });
      } catch (error) {
        console.error('Failed to load Swiper:', error);
      }
    };

    loadSwiper();
  }, []);

  const { Swiper, SwiperSlide, Navigation } = SwiperComponent || {};

  return (
    <View>
      <ForCalcX>
        <ForCalcXContent ref={calcXRef} />
      </ForCalcX>

      {SwiperComponent && (
        <Swiper
          modules={[Navigation]}
          spaceBetween={20}
          slidesPerView={countSlide}
          slidesOffsetBefore={marginLeft}
          slidesOffsetAfter={marginRight + finalSlideOffset}
          navigation={{
            prevEl: '.custom-prev',
            nextEl: '.custom-next',
          }}>
          {slides.map((slide) => (
            <SwiperSlide key={slide.id}>
              <Slide>
                <SlideContent slide={slide} />
              </Slide>
            </SwiperSlide>
          ))}
          <SwiperSlide key="final-slide" className="final-slide-slide">
            <Slide id="final-slide">
              <FinalSlide />
            </Slide>
          </SwiperSlide>
        </Swiper>
      )}
    </View>
  );
};

const View = styled.div`
  .swiper-slide {
    height: auto;
  }

  .final-slide-slide {
    @media ${Points.tablet_landscape} {
      width: 300px !important;
    }

    @media ${Points.laptop_wide} {
      width: 400px !important;
    }
  }
`;
const ForCalcX = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  width: 100%;

  @media ${Points.tablet_landscape} {
    width: 100%;
    max-width: 1024px;
    margin: 0 auto;
    padding: 0 28px;
  }

  @media ${Points.laptop_wide} {
    padding: 0 80px;
    width: 100%;
    max-width: 1600px;
  }

  @media ${Points.fullhd} {
    padding: 0;
  }
`;

const ForCalcXContent = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
`;
const Slide = styled.div`
  width: 100%;
  height: 100%;
`;
